import { Component, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IActividadUsuario,
  IPopulate,
  IQueryParam,
} from 'modelos/src';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../../../auxiliares/tabla/tabla.component';
import { EstadisticasUsoService } from '../../../../auxiliares/servicios/http/estadisticas-uso.service';
import { ClienteJerarquiaService } from '../../../../auxiliares/servicios/cliente-jerarquia.service';

/**
 * Listado de intervalos de inactividad de los usuarios del cliente (y sus
 * sub-clientes). El backend acota por el permiso del request y exige la
 * acción "Administración - Ver inactividad usuarios".
 */
@Component({
  selector: 'app-listado-inactividad-usuarios',
  templateUrl: './listado-inactividad-usuarios.component.html',
  standalone: false,
})
export class ListadoInactividadUsuariosComponent implements OnInit {
  helper = inject(HelperService);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private service = inject(EstadisticasUsoService);

  public name = 'ListadoInactividadUsuariosComponent';
  public datos?: IActividadUsuario[];
  public totalCount = 0;
  public columnas?: IColumnas<IActividadUsuario>[];

  public search: IRegExpSearch = {
    fields: ['usuario'],
  };

  private rango: IFiltroTabla = {
    filter: {
      field: 'inicio',
    },
    label: 'Rango de Fechas',
    tipo: 'dateRange',
    // Días completos, igual que el período de Estadísticas de uso.
    soloDia: true,
  };

  private usuario: IFiltroTabla = {
    filter: {
      field: 'usuario',
    },
    label: 'Usuario',
    tipo: 'regexp',
  };

  // Duración mínima del intervalo (fin - inicio), filtrada en Mongo con
  // $expr. Los intervalos abiertos (sin fin) quedan afuera: su duración es
  // desconocida (en la tabla se muestran como "Sin duración").
  private duracionMinima: IFiltroTabla = {
    filter: {
      field: '$expr',
    },
    label: 'Duración mínima',
    tipo: 'select',
    elementos: [5, 15, 30, 60, 120, 240, 480].map((min) => ({
      label:
        min < 60 ? `${min} minutos` : `${min / 60} hora${min > 60 ? 's' : ''}`,
      value: { $gte: [{ $subtract: ['$fin', '$inicio'] }, min * 60_000] },
    })),
    selectLabel: 'label',
    selectValue: 'value',
    compararPorJson: true,
  };

  // FILTROS de cliente. Propietario = coincidencia exacta; jerarquía = ese
  // cliente y toda su descendencia (idsAncestros no incluye al propio).
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  public filtros: IFiltroTabla[] = [
    this.rango,
    this.usuario,
    this.duracionMinima,
  ];

  private filter: IFilter<IActividadUsuario> = {};
  private populate: IPopulate = [
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-inicio',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    try {
      const data = await this.service.getInactividad(this.query);
      this.datos = data.datos;
      this.totalCount = data.totalCount ?? 0;
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private duracion(dato: IActividadUsuario): string {
    if (!dato.inicio || !dato.fin) return '';
    const ms = new Date(dato.fin).getTime() - new Date(dato.inicio).getTime();
    if (ms < 0) return '';
    const totalMin = Math.floor(ms / 60_000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h ? `${h} h ${m} min` : `${m} min`;
  }

  private setColumnas() {
    this.columnas = [
      // Usuario
      {
        header: {
          label: 'Usuario',
        },
        row: {
          field: 'usuario',
          noData: 'Sin usuario',
        },
      },
      // Cliente
      {
        header: {
          label: 'Cliente',
        },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      },
      // Inicio
      {
        header: {
          label: 'Inicio de inactividad',
        },
        row: {
          fecha: (dato) => dato.inicio,
          hace: true,
        },
      },
      // Fin
      {
        header: {
          label: 'Volvió a estar activo',
        },
        row: {
          fecha: (dato) => dato.fin,
          noData: 'Sin actividad posterior',
        },
      },
      // Duración
      {
        header: {
          label: 'Duración',
        },
        row: {
          parse: (dato) => this.duracion(dato),
          noData: 'Sin duración',
        },
      },
    ];
  }

  // Hooks

  async ngOnInit() {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
    }
    this.setColumnas();
    await this.listar();
  }
}
